/**
 * PERFORMANCE: Persistent texture cache with localStorage
 * Data persists across page refreshes and browser sessions
 * 
 * CROSS-TAB SYNC: Uses BroadcastChannel to notify other tabs when caches are invalidated
 */

// Cache TTL in milliseconds (24 hours - long-lived for better UX)
const CACHE_TTL = 24 * 60 * 60 * 1000;

// localStorage keys
const STORAGE_KEYS = {
  highRes: 'lumenforge_highres_cache',
  highResTimestamp: 'lumenforge_highres_timestamp',
  vanilla: 'lumenforge_vanilla_cache_v3',
  vanillaTimestamp: 'lumenforge_vanilla_timestamp_v3'
};

// In-memory fallback for thumbnails
const thumbnailCache = new Map();

// BroadcastChannel for cross-tab communication
const BROADCAST_CHANNEL_NAME = 'lumenforge_cache_sync';
let broadcastChannel = null;

// Callbacks registered by components to handle cache invalidation
const invalidationCallbacks = new Set();

/**
 * Initialize BroadcastChannel for cross-tab sync
 */
const initBroadcastChannel = () => {
  if (broadcastChannel) return broadcastChannel;
  
  try {
    broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    broadcastChannel.onmessage = (event) => {
      const { type, payload } = event.data || {};
      console.log("[BroadcastChannel] Received message:", type, payload);
      
      if (type === 'CACHE_INVALIDATED') {
        // IMPORTANT: Also clear localStorage in receiving tab
        // This ensures loadHighResTextures(true) actually fetches fresh data
        try {
          if (payload?.type === 'all' || payload?.type === 'highres') {
            localStorage.removeItem(STORAGE_KEYS.highRes);
            localStorage.removeItem(STORAGE_KEYS.highResTimestamp);
          }
          if (payload?.type === 'all' || payload?.type === 'vanilla') {
            localStorage.removeItem(STORAGE_KEYS.vanilla);
            localStorage.removeItem(STORAGE_KEYS.vanillaTimestamp);
          }
          console.log("[BroadcastChannel] Cleared localStorage cache in receiving tab");
        } catch (e) {
          console.warn("[BroadcastChannel] Failed to clear localStorage:", e);
        }
        
        // Clear local in-memory caches
        thumbnailCache.clear();
        
        // Notify all registered callbacks
        invalidationCallbacks.forEach(callback => {
          try {
            callback(payload);
          } catch (e) {
            console.error("[BroadcastChannel] Callback error:", e);
          }
        });
      }
    };
    console.log("[BroadcastChannel] Initialized for cross-tab sync");
  } catch (e) {
    console.warn("[BroadcastChannel] Not supported:", e);
  }
  
  return broadcastChannel;
};

// Initialize on module load
initBroadcastChannel();

/**
 * Register a callback to be notified when caches are invalidated from another tab
 * @param {Function} callback - Function to call when invalidation occurs
 * @returns {Function} - Unsubscribe function
 */
export const onCacheInvalidated = (callback) => {
  invalidationCallbacks.add(callback);
  console.log("[Cache] Registered invalidation callback, total:", invalidationCallbacks.size);
  
  // Return unsubscribe function
  return () => {
    invalidationCallbacks.delete(callback);
    console.log("[Cache] Unregistered invalidation callback, remaining:", invalidationCallbacks.size);
  };
};

/**
 * Broadcast cache invalidation to all other tabs
 */
const broadcastInvalidation = (payload = {}) => {
  if (broadcastChannel) {
    try {
      broadcastChannel.postMessage({ type: 'CACHE_INVALIDATED', payload });
      console.log("[BroadcastChannel] Sent invalidation to other tabs");
    } catch (e) {
      console.warn("[BroadcastChannel] Failed to broadcast:", e);
    }
  }
};

/**
 * Check if cache is valid (not expired)
 */
const isCacheValid = (timestamp) => {
  if (!timestamp) return false;
  return Date.now() - timestamp < CACHE_TTL;
};

/**
 * Safe localStorage get (handles quota errors)
 */
const safeGetStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    console.warn("[Cache] localStorage read failed:", e);
    return null;
  }
};

/**
 * Safe localStorage set (handles quota errors)
 */
const safeSetStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    console.warn("[Cache] localStorage write failed (quota?):", e);
    // Try to clear old caches to make room
    try {
      localStorage.removeItem(STORAGE_KEYS.highRes);
      localStorage.removeItem(STORAGE_KEYS.vanilla);
      localStorage.setItem(key, value);
      return true;
    } catch (e2) {
      return false;
    }
  }
};

/**
 * Get cached high-res library data
 */
export const getCachedHighResLibrary = () => {
  const timestamp = parseInt(safeGetStorage(STORAGE_KEYS.highResTimestamp) || '0', 10);
  if (isCacheValid(timestamp)) {
    const data = safeGetStorage(STORAGE_KEYS.highRes);
    if (data) {
      try {
        console.log("[Cache] High-res library HIT (localStorage)");
        return JSON.parse(data);
      } catch (e) {
        console.warn("[Cache] Failed to parse high-res cache");
      }
    }
  }
  console.log("[Cache] High-res library MISS");
  return null;
};

/**
 * Set high-res library cache
 */
export const setCachedHighResLibrary = (data) => {
  const jsonData = JSON.stringify(data);
  if (safeSetStorage(STORAGE_KEYS.highRes, jsonData)) {
    safeSetStorage(STORAGE_KEYS.highResTimestamp, Date.now().toString());
    console.log("[Cache] High-res library cached:", data?.length || 0, "items");
  }
};

/**
 * Get cached vanilla textures data
 */
export const getCachedVanillaTextures = () => {
  const timestamp = parseInt(safeGetStorage(STORAGE_KEYS.vanillaTimestamp) || '0', 10);
  if (isCacheValid(timestamp)) {
    const data = safeGetStorage(STORAGE_KEYS.vanilla);
    if (data) {
      try {
        console.log("[Cache] Vanilla textures HIT (localStorage)");
        return JSON.parse(data);
      } catch (e) {
        console.warn("[Cache] Failed to parse vanilla cache");
      }
    }
  }
  console.log("[Cache] Vanilla textures MISS");
  return null;
};

/**
 * Set vanilla textures cache
 */
export const setCachedVanillaTextures = (data) => {
  const jsonData = JSON.stringify(data);
  if (safeSetStorage(STORAGE_KEYS.vanilla, jsonData)) {
    safeSetStorage(STORAGE_KEYS.vanillaTimestamp, Date.now().toString());
    console.log("[Cache] Vanilla textures cached:", data?.textures?.length || 0, "items");
  }
};

/**
 * Pre-load thumbnail image (lazy - only when visible)
 */
export const preloadThumbnail = (url) => {
  if (thumbnailCache.has(url)) return;
  
  const img = new window.Image();
  img.src = url;
  thumbnailCache.set(url, img);
};

/**
 * Pre-load multiple thumbnails in background (ALL of them)
 * Uses requestIdleCallback for better performance
 */
export const preloadThumbnails = (urls) => {
  // Preload ALL thumbnails, not just 50 - use staggered loading
  let loaded = 0;
  const loadBatch = (startIndex) => {
    const batchSize = 20;
    const endIndex = Math.min(startIndex + batchSize, urls.length);
    
    for (let i = startIndex; i < endIndex; i++) {
      preloadThumbnail(urls[i]);
      loaded++;
    }
    
    // Schedule next batch if more to load
    if (endIndex < urls.length) {
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(() => loadBatch(endIndex), { timeout: 100 });
      } else {
        setTimeout(() => loadBatch(endIndex), 50);
      }
    } else {
      console.log(`[Cache] Preloaded ${loaded} thumbnails`);
    }
  };
  
  // Start loading
  loadBatch(0);
};

/**
 * Check if a thumbnail is already cached
 */
export const isThumbnailCached = (url) => {
  return thumbnailCache.has(url);
};

/**
 * Invalidate all caches (call after uploads)
 * @param {boolean} broadcast - Whether to notify other tabs (default: true)
 */
export const invalidateAllCaches = (broadcast = true) => {
  try {
    localStorage.removeItem(STORAGE_KEYS.highRes);
    localStorage.removeItem(STORAGE_KEYS.highResTimestamp);
    localStorage.removeItem(STORAGE_KEYS.vanilla);
    localStorage.removeItem(STORAGE_KEYS.vanillaTimestamp);
  } catch (e) {
    console.warn("[Cache] Failed to clear localStorage:", e);
  }
  
  // Clear in-memory thumbnail cache
  thumbnailCache.clear();
  
  console.log("[Cache] All caches invalidated");
  
  // Broadcast to other tabs
  if (broadcast) {
    broadcastInvalidation({ type: 'all' });
  }
};

/**
 * Invalidate specific cache
 * @param {string} type - Cache type ('highres' or 'vanilla')
 * @param {boolean} broadcast - Whether to notify other tabs (default: true)
 */
export const invalidateCache = (type, broadcast = true) => {
  try {
    if (type === "highres") {
      localStorage.removeItem(STORAGE_KEYS.highRes);
      localStorage.removeItem(STORAGE_KEYS.highResTimestamp);
    } else if (type === "vanilla") {
      localStorage.removeItem(STORAGE_KEYS.vanilla);
      localStorage.removeItem(STORAGE_KEYS.vanillaTimestamp);
    }
    console.log(`[Cache] ${type} cache invalidated`);
    
    // Broadcast to other tabs
    if (broadcast) {
      broadcastInvalidation({ type });
    }
  } catch (e) {
    console.warn("[Cache] Failed to invalidate:", e);
  }
};

export default {
  getCachedHighResLibrary,
  setCachedHighResLibrary,
  getCachedVanillaTextures,
  setCachedVanillaTextures,
  preloadThumbnail,
  preloadThumbnails,
  invalidateAllCaches,
  invalidateCache,
  onCacheInvalidated,
};
